import { create } from 'zustand';
import { updateUserSettings, updateCurrentUserSettings, UserSettingsUpdate } from '@/api/userSettings';
import { getPendingPermissionRequestsCount } from '@/api/permissionRequests';
import { Conversation, DataSources, DEFAULT_DATA_SOURCES } from '@/models/conversation';
import { Model } from '@/models/model';
import { Agent } from '@/models/agent';
import { UserSettings } from '@/api/userSettings';

const canReviewPermissionRequests = (settings: UserSettings | null): boolean => {
  if (!settings) {
    return false;
  }

  return settings.role === 'admin' || settings.role === 'manager' || settings.is_admin === true;
};

const delay = (ms: number) => new Promise<void>((resolve) => {
  window.setTimeout(resolve, ms);
});

interface User {
  name: string;
  picture?: string;
  isAdmin?: boolean;
  id?: string; // User ID from the authentication provider (oid or sub)
  pictureVersion?: number; // Version number to bust cache when profile picture changes
}

interface AccordionState {
  recents: boolean;
  favorites: boolean;
  agents: boolean;
  features: boolean;
}

interface AppState {
  user: User | null;
  userSettings: UserSettings | null;
  sidebarOpen: boolean;  
  modelList: Model[];
  selectedModel: string;
  agentList: Agent[];
  selectedAgent: string | null;
  featureList: any[];
  conversationList: Conversation[];
  selectedConversation: Conversation | null;
  messages: any[];
  message: string;
  accordionState: AccordionState;
  themeMode: 'light' | 'dark';
  currentFeature: string | null;
  dataSources: DataSources;
  thinkingMode: 'auto' | 'quick' | 'deep';
  pendingPermissionRequestsCount: number;
  
  setUser: (user: User | null) => void;
  setUserSettings: (settings: UserSettings | null) => void;
  setSidebarOpen: (open: boolean) => void;
  setModelList: (models: Model[]) => void;
  setSelectedModel: (modelId: string) => void;  setAgentList: (agents: Agent[]) => void;
  setSelectedAgent: (agentId: string | null) => void;
  setFeatureList: (features: any[]) => void;
  setConversationList: (list: Conversation[]) => void;
  addNewConversation: (conversation: Conversation) => void;
  setSelectedConversation: (conv: Conversation | null) => void;  setMessages: (messages: any[]) => void;
  setMessage: (msg: string) => void;
  setAccordionState: (section: keyof AccordionState, isOpen: boolean) => void;
  setCurrentFeature: (feature: string | null) => void;
  setThemeMode: (mode: 'light' | 'dark') => void;
  toggleTheme: () => void;
  setDataSources: (sources: DataSources) => void;
  toggleDataSource: (key: keyof DataSources) => void;
  setThinkingMode: (mode: 'auto' | 'quick' | 'deep') => void;
  setPendingPermissionRequestsCount: (count: number) => void;
  refreshPendingPermissionRequestsCount: () => Promise<void>;
  decrementPendingPermissionRequestsCount: () => void;
}

export const useAppStore = create<AppState>((set) => ({
  user: null,
  userSettings: null,
  sidebarOpen: false, // Default to closed, will be set from backend on app init
  modelList: [],
  selectedModel: (() => {
    const saved = localStorage.getItem('selectedModel');
    return saved || '';
  })(), // Initialize from localStorage, default to empty string
  agentList: [],
  selectedAgent: null,
  featureList: [],
  conversationList: [],
  selectedConversation: null,
  messages: [],
  message: '',
  currentFeature: (() => {
    const saved = localStorage.getItem('currentFeature');
    return saved || null;
  })(), // Initialize from localStorage, default to null
  // Initialize themeMode default to 'light' (will be set from backend on app init)
  themeMode: 'light',
  dataSources: { ...DEFAULT_DATA_SOURCES },
  pendingPermissionRequestsCount: 0,
  thinkingMode: (() => {
    const saved = localStorage.getItem('thinkingMode');
    return (saved === 'quick' || saved === 'deep') ? saved : 'auto';
  })() as 'auto' | 'quick' | 'deep',
  accordionState: {
    recents: false,
    favorites: false,
    agents: false,
    features: false
  },
  
  setUser: (user) => set((state) => ({
    user,
    pendingPermissionRequestsCount: user ? state.pendingPermissionRequestsCount : 0,
  })),
  setUserSettings: (settings) => {
    // Only initialize sidebar state from backend on INITIAL load (when userSettings was null)
    // This prevents sidebar state from being reset when other settings are updated
    const currentState = (useAppStore as any).getState();
    const isInitialLoad = currentState.userSettings === null;
    
    if (isInitialLoad && settings?.sidebar_collapsed !== undefined) {
      set({
        userSettings: settings,
        sidebarOpen: !settings.sidebar_collapsed,
        pendingPermissionRequestsCount: canReviewPermissionRequests(settings)
          ? currentState.pendingPermissionRequestsCount
          : 0,
      });
    } else {
      set({
        userSettings: settings,
        pendingPermissionRequestsCount: canReviewPermissionRequests(settings)
          ? currentState.pendingPermissionRequestsCount
          : 0,
      });
    }
  },
  setSidebarOpen: (open) => {
    set({ sidebarOpen: open });
    // Persist to backend if we have a user id in store (fire-and-forget)
    try {
      const state = (useAppStore as any).getState();
      const userId = state.user?.id;
      if (userId) {
        updateUserSettings(userId, { sidebar_collapsed: !open } as UserSettingsUpdate).catch(err => {
          console.error('Failed to persist sidebar state:', err);
        });
      }
    } catch (err) {
      console.error('Error persisting sidebar state:', err);
    }
  },  setModelList: (models) => set({ modelList: models }),
  setSelectedModel: (modelId) => {
    localStorage.setItem('selectedModel', modelId);
    set({ selectedModel: modelId });
  },
  setAgentList: (agents) => set({ agentList: agents }),
  setSelectedAgent: (agentId) => set({ selectedAgent: agentId }),
  setFeatureList: (features) => set({ featureList: features }),
  setConversationList: (list) => set({ conversationList: list }),
  addNewConversation: (conversation) => set((state) => ({ conversationList: [...state.conversationList, conversation] })),
  setSelectedConversation: (conv) => set({
    selectedConversation: conv,
    // Always sync dataSources toggles from the conversation being selected so the
    // UI always reflects the state that was active when that conversation was used.
    // Falls back to all-OFF (DEFAULT_DATA_SOURCES) for new or legacy conversations.
    dataSources: conv?.data_sources ?? { mail: false, sharepoint: false, teams_chats: false, teams_meetings: false, people: false },
  }),  setMessages: (messages) => set({ messages }),
  setMessage: (msg) => set({ message: msg }),
  setAccordionState: (section, isOpen) => set((state) => ({
    accordionState: {
      ...state.accordionState,
      [section]: isOpen
    }
  })),
  setCurrentFeature: (feature) => {
    if (feature) {
      localStorage.setItem('currentFeature', feature);
    } else {
      localStorage.removeItem('currentFeature');
    }
    set({ currentFeature: feature });
  },
  setThemeMode: (mode) => {
    set({ themeMode: mode });
    // Persist to backend using current-user endpoint so all users can save preference
    try {
      // Fire-and-forget persistence
      updateCurrentUserSettings({ theme_preference: mode } as UserSettingsUpdate).catch(err => {
        console.error('Failed to persist theme preference:', err);
      });
    } catch (err) {
      console.error('Error persisting theme preference:', err);
    }
  },
  toggleTheme: () => set((state) => {
    const newMode = state.themeMode === 'light' ? 'dark' : 'light';
    // Persist via setThemeMode which handles backend update
    (useAppStore as any).getState().setThemeMode(newMode);
    return { themeMode: newMode };
  }),
  setDataSources: (sources) => set({ dataSources: sources }),
  toggleDataSource: (key) => set((state) => ({
    dataSources: { ...state.dataSources, [key]: !state.dataSources[key] }
  })),
  setThinkingMode: (mode) => {
    localStorage.setItem('thinkingMode', mode);
    set({ thinkingMode: mode });
  },
  setPendingPermissionRequestsCount: (count) => {
    set({ pendingPermissionRequestsCount: Math.max(0, count) });
  },
  refreshPendingPermissionRequestsCount: async () => {
    const state = (useAppStore as any).getState();
    const canReview = canReviewPermissionRequests(state.userSettings);

    if (!canReview) {
      set({ pendingPermissionRequestsCount: 0 });
      return;
    }

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const count = await getPendingPermissionRequestsCount(true);
        set({ pendingPermissionRequestsCount: Math.max(0, count) });
        return;
      } catch (error) {
        if (attempt === maxAttempts) {
          console.error('Failed to refresh pending permission requests count:', error);
          return;
        }

        // Give auth/session state a short moment to settle during login bootstrap.
        await delay(800 * attempt);
      }
    }
  },
  decrementPendingPermissionRequestsCount: () => {
    set((state) => ({ pendingPermissionRequestsCount: Math.max(0, state.pendingPermissionRequestsCount - 1) }));
  },
}));